import { useEffect, useState } from 'react';
import { getHolidayTypes } from '../../../../api/geo/useHolidayTypes';
import axiosInstance from '../../../../api/axiosInstance';

/**
 * What the header editor reads besides its own settings, once per visit:
 *  - every holiday type (active or not) with its public slug, so a hand-picked type that has been
 *    switched off can be flagged and the preview can resolve the menu the way the server does;
 *  - the homepage's popular-destination groups, which fill the Destinations menu when its source
 *    is "Homepage groups".
 * A failure leaves that part empty and is reported once through `onError`.
 */
export function useHeaderLookups({ onError } = {}) {
  const [holidayTypes, setHolidayTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [homepageGroups, setHomepageGroups] = useState(null);
  const [homepageLoading, setHomepageLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    // The dashboard list carries every type but, on the deployed backend, no slug; the website's
    // own list carries the slug (the real /holidays/:slug handle, which a renamed type no longer
    // derives from its name) for the active types. Merged by id, so the editor shows the link a
    // visitor will actually open. Inactive types link nowhere, so they need none. The slug list
    // is a nicety: if it fails, the editor still works and the preview derives from the name.
    const slugs = axiosInstance
      .get('/website/holiday-types')
      .then(({ data }) => new Map((data?.data || []).map((t) => [Number(t.id), t.slug])))
      .catch(() => new Map());
    Promise.all([getHolidayTypes({ limit: 100 }), slugs])
      .then(([res, slugById]) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        if (alive) setHolidayTypes(list.map((t) => ({ ...t, slug: t.slug || slugById.get(Number(t.id)) || '' })));
      })
      .catch(() => alive && onError?.('Failed to load holiday types'))
      .finally(() => alive && setTypesLoading(false));

    axiosInstance
      .get('/cms/layout/homepage-config')
      .then(({ data }) => {
        const groups = data?.data?.homepageConfig?.popularDestinationGroups;
        if (alive) setHomepageGroups(Array.isArray(groups) ? groups : []);
      })
      .catch(() => alive && onError?.('Failed to load the homepage destinations for the preview'))
      .finally(() => alive && setHomepageLoading(false));

    return () => {
      alive = false;
    };
    // Once per visit: onError is a toast, and a new one each render must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { holidayTypes, typesLoading, homepageGroups, homepageLoading };
}
